/**
 * Learn content — design/PROTOTYPE-SPEC.md §15.
 *
 * ## This is adapted, not ported
 *
 * The prototype has 21 guides and 8 FAQs. Copying them verbatim would ship help
 * that is **wrong about this product**, because the production build
 * deliberately differs from the prototype in ways the spec itself mandates:
 *
 *   - Its team FAQ says staff do not need their own accounts and you switch
 *     person in the sidebar. Here every member has a real login — the spec's own
 *     §14 says the production auth model *supersedes* the prototype's shared
 *     one. Shipping the old answer would tell a clinic to run their audit trail
 *     in a way the app does not support.
 *   - Its notifications FAQ describes email, WhatsApp and Telegram channels.
 *     None of that exists; there is no notification settings table and no digest
 *     job.
 *   - Its plans FAQ sells the AI marketing assistant, which was cut, and says
 *     you switch plan "from the sidebar", which is the Account page here.
 *   - Several guides walk through the quick-add catalogue, which is
 *     demo-account-only.
 *
 * Help that confidently describes something the product does not do is worse
 * than no help: it costs a support conversation *and* trust. So the guides below
 * only reference screens that exist, and every step's anchor is a real
 * `data-tour` attribute — asserted by `content.test.ts` against the tour's
 * anchor list, so a page restructure that drops an anchor fails the build rather
 * than silently breaking a guide.
 */
import type { Page } from '../Sidebar.js';

/** Every `data-tour` anchor that exists in the app. Keep in sync with the JSX. */
export const ANCHORS = [
  'kpis',
  'addstock',
  'table',
  'expiry',
  'stocktake',
  'movements',
  'audit',
  'analytics',
  'promos',
  'monthbar',
  'team',
  'manage',
  'plancard',
  'learn',
] as const;

export type Anchor = (typeof ANCHORS)[number];

export interface Guide {
  id: string;
  /** Grouping heading in the list. */
  area: string;
  title: string;
  /** Rough reading time, as the prototype shows. */
  mins: string;
  page: Page;
  /** One anchor per step. Same length as `steps`. */
  anchors: Anchor[];
  steps: string[];
  /** Gated guides are hidden from plans that cannot follow them. */
  requires?: 'analytics' | 'promotions' | 'targets';
}

export const GUIDES: Guide[] = [
  {
    id: 'g_dashboard',
    area: 'Getting started',
    title: 'Read your dashboard',
    mins: '1 min',
    page: 'dashboard',
    anchors: ['kpis', 'kpis'],
    steps: [
      'The dark card is the total value of everything on your shelves. The three beside it are the ones to act on: what has fallen to or below its minimum, what expires within twelve weeks, and what needs nothing.',
      'Below, “Reorder now” and “Expiring soon” list the actual products behind those numbers. Click any row to open it.',
    ],
  },
  {
    id: 'g_addstock',
    area: 'Getting started',
    title: 'Book in a delivery',
    mins: '1 min',
    page: 'dashboard',
    anchors: ['addstock', 'addstock'],
    steps: [
      '“Add stock” is in the header on every page, so booking in a delivery never means navigating somewhere first.',
      'Enter the quantity, lot number and expiry date. That creates a tracked batch, which is what makes expiry warnings and lot tracing possible later.',
    ],
  },
  {
    id: 'g_inventory',
    area: 'Stock',
    title: 'Find and use stock',
    mins: '1 min',
    page: 'inventory',
    anchors: ['table', 'table'],
    steps: [
      'Every product you carry, with live quantity, stock status and the expiry of its earliest batch.',
      '“Log use” deducts from the earliest-expiring batch first, automatically. You never choose a batch, and stock cannot go negative.',
    ],
  },
  {
    id: 'g_expiry',
    area: 'Stock',
    title: 'Stay ahead of expiry',
    mins: '2 min',
    page: 'expiry',
    anchors: ['expiry', 'expiry'],
    steps: [
      'Batches grouped by urgency: expired, going soon, and in date. This is the page that turns waste into something you saw coming.',
      'Writing off expired stock deducts it and records the reason. Nothing is ever removed automatically: your records only change when a person confirms it.',
    ],
  },
  {
    id: 'g_count',
    area: 'Stock',
    title: 'Run a stock count',
    mins: '2 min',
    page: 'count',
    anchors: ['stocktake', 'stocktake'],
    steps: [
      'Walk the shelf and type what you can physically see. Leave anything you have not counted blank.',
      'Differences need a reason before they apply. That is what makes a count an explanation rather than a silent correction. It is also how you fix a typo without editing history.',
    ],
  },
  {
    id: 'g_import',
    area: 'Stock',
    title: 'Bring in a spreadsheet',
    mins: '2 min',
    page: 'manage',
    anchors: ['manage', 'manage'],
    steps: [
      'Import accepts .xlsx and .csv. Download the template if you want the exact column names, though common variations are matched automatically.',
      'Products you already have are skipped rather than duplicated, so re-importing a corrected file is safe.',
    ],
  },
  {
    id: 'g_activity',
    area: 'Records',
    title: 'Trace a lot number',
    mins: '1 min',
    page: 'activity',
    anchors: ['movements', 'movements'],
    steps: [
      'Every movement in, out or written off, with who did it, when, and why.',
      'Search a lot number to see everywhere it went. This is what a recall looks like in practice, and it works without storing anything about a patient.',
    ],
  },
  {
    id: 'g_audit',
    area: 'Records',
    title: 'Use the audit log',
    mins: '1 min',
    page: 'audit',
    anchors: ['audit', 'audit'],
    steps: [
      'Everything that is not a stock movement: who changed the plan, the VAT rate, someone’s role, who signed in.',
      'Filter by person, category or date. Nothing here can be edited or deleted. A correction is a new entry explaining the old one, which is what makes it worth anything in an inspection.',
    ],
  },
  {
    id: 'g_team',
    area: 'Settings',
    title: 'Add your team',
    mins: '2 min',
    page: 'team',
    anchors: ['team', 'team'],
    steps: [
      'Invite someone by name and email and they get their own login. Everyone having their own account is what keeps the audit trail honest.',
      'Roles decide what they can do: billing is owner-only; voids, stock counts and team changes need manager; logging usage is everyone. People are deactivated, never deleted, so past entries keep their author.',
    ],
  },
  {
    id: 'g_analytics',
    area: 'Grow',
    title: 'See where the money sits',
    mins: '2 min',
    page: 'analytics',
    anchors: ['analytics', 'analytics'],
    steps: [
      'The top row is money: what is on the shelf, what you bought, what you used or sold, and what you wrote off. Switch between 30 days, 90 days and 12 months at the top right.',
      'Further down, "How long your stock will last" shows the days of cover for each product and roughly when it runs out. "Stock sitting still" lists what nobody has touched, which is worth a promotion before it becomes a write-off.',
    ],
    requires: 'analytics',
  },
  {
    id: 'g_promotions',
    area: 'Grow',
    title: 'Run a promotion',
    mins: '2 min',
    page: 'promotions',
    anchors: ['promos', 'promos'],
    steps: [
      'Suggestions are built from your own shelves: lines that are expiring, slow, or overstocked.',
      'A promotion is a percentage or a fixed amount off, between two dates. Sales made under it are attributed to it, so you can see afterwards whether it paid.',
    ],
    requires: 'promotions',
  },
  {
    id: 'g_targets',
    area: 'Grow',
    title: 'Set a monthly target',
    mins: '3 min',
    page: 'targets',
    anchors: ['monthbar', 'monthbar'],
    steps: [
      'Your fixed costs plus your profit goal is what the month has to bring in. The bar shows break-even and target as two marks, and where you are against them.',
      'Below, each treatment shows how many you would need to get there. Logging one is a tap, and it stores a count and nothing else. No patient data, ever.',
    ],
    requires: 'targets',
  },
  {
    id: 'g_plan',
    area: 'Settings',
    title: 'Change your plan',
    mins: '1 min',
    page: 'account',
    anchors: ['plancard', 'plancard'],
    steps: [
      'Compare plans and switch between monthly and yearly. Yearly is two months free.',
      'Payment details, invoices, VAT receipts and cancellation are all handled by Stripe. We never see your card.',
    ],
  },
];

export interface Faq {
  id: string;
  q: string;
  a: string;
}

/**
 * Rewritten rather than ported. Every answer below is true of *this* build —
 * see the note at the top of this file for the four that were not.
 */
export const FAQS: Faq[] = [
  {
    id: 'f_expired',
    q: 'What happens when a product expires?',
    a: 'Nothing is removed automatically. Your records only change when a person confirms it. Expired batches are flagged on the Expiry page, and writing one off deducts the stock and records the reason in the audit trail.',
  },
  {
    id: 'f_batches',
    q: 'Why does it track batches and lot numbers?',
    a: 'Each delivery has its own expiry date and lot number. Tracking them separately is what makes expiry warnings accurate, lets stock be used earliest-expiring-first, and means a recalled lot can be traced to exact dates and staff, without storing anything about a patient.',
  },
  {
    id: 'f_fefo',
    q: 'Which batch does “Log use” take from?',
    a: 'Always the earliest-expiring batch with stock in it, and it will split across batches if one does not cover the amount. You never pick a batch, and stock cannot go negative.',
  },
  {
    id: 'f_typo',
    q: 'I mistyped a quantity. How do I fix it?',
    a: 'Run a stock count on that product, enter the correct number and choose a reason. The correction is recorded as an adjustment rather than silently edited, which is what keeps the history trustworthy.',
  },
  {
    id: 'f_void',
    q: 'Can I undo a movement?',
    a: 'You can void it, which records a reversing entry pointing at the original. Both stay visible. Nothing in the movement ledger is ever edited or deleted. The database itself refuses.',
  },
  {
    id: 'f_import',
    q: 'Can I bring my existing spreadsheet in?',
    a: 'Yes. Import & export accepts .xlsx and .csv. There is a template with the exact column names, though common variations are matched automatically. Products you already have are skipped rather than duplicated.',
  },
  {
    id: 'f_expiry_emails',
    q: 'How do I change or stop the stock emails?',
    a: 'We email owners and managers when stock has expired, is about to, or has dropped to its minimum. Open Account and look for Stock emails. Choose weekly, daily or off. It is your own setting and does not change what colleagues receive. Every one of those emails also has a link at the bottom that turns them off in one click, without signing in. Owners and managers get these emails; the dashboard shows the same stock to everyone.',
  },
  {
    id: 'f_team',
    q: 'Do my staff need their own logins?',
    a: 'Yes. Everyone gets their own login, and that is deliberate: every action is recorded against a real person, so the audit trail means something. Invite them from the Team page. On a shared front-desk device you can switch between people with a PIN instead of a full sign-in.',
  },
  {
    id: 'f_audit',
    q: 'Can I edit or delete the audit log?',
    a: 'No, and neither can we. It is append-only at the database level, not just in the app. Corrections are made by adding an entry that explains the old one. That is what makes it worth anything for an inspection.',
  },
  {
    id: 'f_patient',
    q: 'Does it store any patient information?',
    a: 'No. Treatment logging records a count and nothing else: no names, no notes, no dates of birth. That is a deliberate design choice: it keeps the product entirely clear of special-category health data under UK GDPR.',
  },
  {
    id: 'f_plans',
    q: 'What is in each plan?',
    a: 'Starter covers inventory, batch and expiry tracking, stock counts and spreadsheet import. Pro adds Analytics and Promotions. Premium adds Targets, plus unlimited products and people. You can compare and switch on the Account page at any time.',
  },
  {
    id: 'f_export',
    q: 'Can I get my data out?',
    a: 'At any time, from Import & export. It produces a spreadsheet of your current stock. Your data is yours, and leaving should never be difficult.',
  },
];
